import React from "react";
import { TablePagination } from "../components/table";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useLoaderData, useRevalidator } from "react-router";
import { useTranslation } from "react-i18next";
import * as qs from "../qs";
import * as api from "../services/api";
import * as p from "../parser";
import ButtonLoader from "../components/ButtonLoader";
import { formData, FormErrorList, useFormAction } from "../form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import Link from "../components/Link";
import AutocompleteSelect from "../components/AutocompleteSelect.jsx";
import { currentLocationFromRequest } from "../components/LocationSelector";

const defaults = [
  [["page", "limit"], 10],
  [["page", "offset"], 0],
];

const parsers = [
  [["page", "limit"], (val) => p.parseInt(val, 10)],
  [["page", "offset"], (val) => p.parseInt(val, 0)],
];

function graphqlArgs(request) {
  return {
    ...qs.parse(new URL(request.url).searchParams.toString(), {
      defaults: defaults,
      parsers: parsers,
    }),
    filter: {
      location_id: { eq: currentLocationFromRequest(request) },
    },
  };
}

export async function loader({ request }) {
  const location_id = currentLocationFromRequest(request);
  if (location_id == null) {
    return { calendars: {} };
  }

  return {
    calendars: await api.loadCalendars(
      graphqlArgs(request),
      api.contextFromRequest(request),
    ),
  };
}

function useLocationOptions() {
  const [options, setOptions] = React.useState();
  React.useEffect(() => {
    (async () => {
      const res = await api.locationOptions();
      setOptions(res.data.locations.data);
    })();
  }, []);

  return { options: options ?? [], loading: options == null };
}

export function NewResourceDialog({ close }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const locationOptions = useLocationOptions();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.createCalendar(formData(e.currentTarget)),
    () => {
      revalidator.revalidate();
      close();
    },
  );

  return (
    <Dialog open={true} onClose={() => close()}>
      <DialogTitle>{t("resources.new")}</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap="1rem">
            <TextField name="name" label={t("resources.name")} required />
            <AutocompleteSelect
              required
              name="location_id"
              loading={locationOptions.loading}
              options={locationOptions.options}
              label={t("resources.location")}
            />
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => close()}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default function Resources() {
  const { t } = useTranslation();
  const loaderData = useLoaderData();
  const [state, setState] = React.useState({ createResource: false });
  const calendarsConnection = loaderData.calendars?.data?.calendars;
  const { createResource } = state;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h1">Zdroje</Typography>
        <Button
          onClick={() =>
            setState((state) => ({ ...state, createResource: true }))
          }
          variant="outlined"
        >
          {t("resources.new")}
        </Button>
      </Box>
      <Card sx={{ overflow: "initial" }}>
        <CardContent>
          <TableContainer sx={{ overflowX: "initial" }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={(theme) => ({
                    "& .MuiTableCell-stickyHeader": { top: theme.spacing(8) },
                  })}
                >
                  <TableCell>{t("resources.name")}</TableCell>
                  <TableCell>{t("resources.visible")}</TableCell>
                  <TableCell>{t("resources.serviceType")}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calendarsConnection?.data?.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <FontAwesomeIcon
                        icon={row.visible ? faEye : faEyeSlash}
                        size="lg"
                      />
                    </TableCell>
                    <TableCell>{row.service_type}</TableCell>
                    <TableCell>
                      <IconButton
                        component={Link}
                        to={`/resources/${row.id}/edit`}
                        size="small"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination connection={calendarsConnection} />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      {createResource && (
        <NewResourceDialog
          close={() =>
            setState((state) => ({ ...state, createResource: false }))
          }
        />
      )}
    </>
  );
}
